/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { useSelector } from 'react-redux';

import Segment from '../../components/Segment';
import Status from '../../components/Status';
import Grid from '../../components/Grid';
import FiltersHeader from '../../components/filtersHeader';
import FilterList from '../../components/FilterList';

import { ModalFilter, Status as StatusInterface, Params } from '../../types';
import { ReduxStates } from '../../redux/reduxTypes';

import { formatDateFilter } from '../../helpers/format';
import {
  handleCrumbRendering,
  calculateSegmentGrid,
  returnParams,
  fetchEndpoint,
  hasAllUndefinedProperties
} from './helpers';

import SegmentContainer from '../../components/SegmentContainer';
import { ReactComponent as CrumbIcon } from '../../assets/icons/crumb-arrow-icon.svg';
import { endpoints, handleFetchEndpoint } from './configs';
import SectionDetailGraph from './components/SectionDetailGraph';
import SectionDetailGrid from './components/SectionDetailGrid';
import { fetchSpgr } from '../Overview/helpers';

interface Props {
  location: any;
}

const SectionDetail = ({ location }: Props): React.ReactElement => {
  const history = useHistory();
  const filters = useSelector((state: ReduxStates) => state.filters);
  const data = useSelector((state: ReduxStates) => state.data);
  const user = useSelector((state: ReduxStates) => state.user);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const client = useSelector((state: ReduxStates) => state.client);

  const [dateFilter, setDateFilter] = useState<string>(filters.dates.range || '');
  const [customRangeFilter, setCustomRangeFilter] = useState<Array<number | string> | null>(filters.dates.custom);
  const [marketFilters, setMarketFilters] = useState<Array<ModalFilter | null>>([]);

  const [status, setStatus] = useState<StatusInterface | null>(null);
  const selectedCurrency = data.selectedCurrency;

  const [chosenSegmentIndex, setChosenSegmentIndex] = useState<number>(location.state ? location.state.index : 0);
  const [segmentsData, setSegmentsData] = useState<Array<any> | null>(null);
  const [netSalesData, setNetSalesData] = useState<Array<any> | null>(null);
  const [amazonShareData, setAmazonShare] = useState<Array<any> | null>(null);
  const [competitiveShareData, setCompetitiveShare] = useState<Array<any> | null>(null);
  const [allSegmentsData, setAllSegmentsData] = useState<Array<any> | null>(null);
  const [spgrData, setSpgrData] = useState<any | null>(null);

  const [showPlanPercentForNetSales, setShowPlanPercentForNetSales] = useState<boolean>(true);
  const [showWidgets, setShowWidgets] = useState<boolean>(true);
  const [displayNoData, setDisplayNoData] = useState<boolean>(false);

  const path = location.pathname.split('/')[2].replace(/-/gi, '').toString();

  // set document title
  document.title = 'Section Details - Ascential Connect';
  //reroute if not in userModules

  useEffect(() => {
    if (user && user.userModules && !user.userModules.includes('Overview')) history.push('/glance');
  }, [user]);

  //set market filters from filters state
  useEffect(() => {
    setMarketFilters(filters.market);
    const marketFilters = [...filters.market];
    const filterNames = marketFilters.map((x) => x?.name);
    if (filterNames.includes('Category') || filterNames.includes('Brand')) {
      setShowPlanPercentForNetSales(false);
    } else {
      setShowPlanPercentForNetSales(true);
    }
    if (
      filters.market?.some(
        (value) =>
          value?.values?.some((value) => value.name.toLowerCase().includes('amazon')) && value?.values?.length === 1
      )
    ) {
      setShowWidgets(true);
    } else {
      setShowWidgets(false);
    }
    // clearLocalData();
  }, [filters.market]);

  const fetchSegmentsData = (path: string | number, params: unknown) => {
    endpoints[path].segments?.forEach(({ endpoint, id, title }) => {
      (async () => {
        try {
          const data = await fetchEndpoint(endpoint, params, title);
          if (path === 'media' && data?.amazonSummary === null && data?.spgr === null) {
            setDisplayNoData(true);
          } else {
            setDisplayNoData(false);
          }
          if (id === 'netSales') {
            setNetSalesData(() => [...handleFetchEndpoint[path].segments(id, data)].sort((a, b) => a.index - b.index));
          } else if (id === 'amazonShare') {
            setAmazonShare(() => [...handleFetchEndpoint[path].segments(id, data)].sort((a, b) => a.index - b.index));
          } else if (id === 'competitiveShare') {
            setCompetitiveShare(() =>
              [...handleFetchEndpoint[path].segments(id, data)].sort((a, b) => a.index - b.index)
            );
          } else {
            setSegmentsData(() => [...handleFetchEndpoint[path].segments(id, data)].sort((a, b) => a.index - b.index));
          }
        } catch (error) {
          console.error(error);
          setStatus({
            state: 'error',
            message: `Something went wrong fetching ${title} segment data. Please try again.`
          });
        }
      })();
    });
  };

  const fetchSpgrData = async (params: Params) => {
    try {
      const spgrData = await fetchSpgr(params);
      const spgrSegment = {
        title: 'Spend As % of Gross <br/> Revenue',
        currencyCode: spgrData.spgr?.currencyCode,
        currentValue: spgrData.spgr?.currentValue,
        currentValueType: spgrData.spgr?.currentValueType,
        targetPercentage: spgrData.spgr?.targetPercentage,
        yoyPercentage: spgrData.spgr?.yoyPercentage,
        ytdPercentage: spgrData.spgr?.ytdPercentage,
        momPercentage: spgrData.spgr?.momPercentage,
        index: 0,
        displayData: ['MoM', 'YoY', 'YtD']
      };
      setSpgrData(spgrSegment);
    } catch (error: any) {
      console.error(error);
      setStatus({
        state: 'error',
        message: `Something went wrong fetching SPGR segment data. Please try again.`
      });
    }
  };

  useEffect(() => {
    setSegmentsData(() => [spgrData, ...(segmentsData ? segmentsData : [])]);
  }, [spgrData]);

  useEffect(() => {
    if ((path === 'share' || path === 'digitalcommercepenetration') && !showWidgets) history.push('/overview');
  }, [showWidgets]);

  // fetch data
  useEffect(() => {
    //reset data
    clearLocalData();
    setDisplayNoData(false);
    if (
      selectedCurrency &&
      marketFilters.length &&
      copyData &&
      dateFilter &&
      data.dateFilterRangeLimit &&
      !((path === 'share' || path === 'digitalcommercepenetration') && !showWidgets)
    ) {
      const { params, path } = returnParams(
        marketFilters,
        formatDateFilter(dateFilter, customRangeFilter || undefined, copyData, data.dateFilterRangeLimit),
        data.dateFilterRangeLimit
      );
      fetchSegmentsData(path, params);
      if (path === 'media' && showWidgets) {
        fetchSpgrData(params);
      }
    }
  }, [selectedCurrency, marketFilters, dateFilter, copyData, customRangeFilter, data.dateFilterRangeLimit]);

  // Clear Selected Graph and Grid data when chosenSegmentIndex changes
  useEffect(() => {
    if (path === 'sales' && !showWidgets) {
      setAllSegmentsData(() => [...(netSalesData || [])]);
    } else if (path === 'media' && !showWidgets) {
      const filteredSegmentsData = segmentsData?.filter(
        (value) => value?.title !== 'Spend As % of Gross <br/> Revenue'
      );
      setAllSegmentsData(() => [...(filteredSegmentsData || [])]);
    } else {
      setAllSegmentsData(() => [
        ...(netSalesData || []),
        ...(amazonShareData || []),
        ...(competitiveShareData || []),
        ...(segmentsData || [])
      ]);
    }
  }, [netSalesData, amazonShareData, competitiveShareData, segmentsData]);

  useEffect(() => {
    if (!showWidgets) {
      const hasChosenIndex = allSegmentsData?.filter((segment) => segment?.index === chosenSegmentIndex);
      if (
        hasChosenIndex?.length === 0 &&
        allSegmentsData &&
        allSegmentsData.length >= 1 &&
        !hasAllUndefinedProperties(allSegmentsData)
      ) {
        setChosenSegmentIndex(allSegmentsData[0]?.index);
      }
    }
  }, [allSegmentsData]);

  const handleSegmentClick = (index: number) => {
    index !== chosenSegmentIndex && setChosenSegmentIndex(index);
  };

  useEffect(() => {
    if (
      allSegmentsData &&
      allSegmentsData?.length > 1 &&
      hasAllUndefinedProperties(allSegmentsData) &&
      !displayNoData
    ) {
      history.push('/overview');
    }
    if (location?.state === undefined && allSegmentsData && !hasAllUndefinedProperties(allSegmentsData)) {
      setChosenSegmentIndex(allSegmentsData[0]?.index);
    } else if (
      allSegmentsData &&
      hasAllUndefinedProperties(allSegmentsData) &&
      chosenSegmentIndex === 0 &&
      !showWidgets
    ) {
      setChosenSegmentIndex(-1);
    }
  }, [allSegmentsData, location.state]);

  const clearLocalData = () => {
    setSegmentsData(null);
    setNetSalesData(null);
    setAmazonShare(null);
    setCompetitiveShare(null);
  };

  return (
    <div className="pt-[30px] px-6 relative pb-[43px]">
      <div className="rounded-[2px]">
        {status && <Status state={status.state} message={status.message} onExit={() => setStatus(null)} />}
        {/* GLOBAL FILTERS */}
        <FiltersHeader
          marketFilters={marketFilters}
          status={status}
          dateFilter={dateFilter}
          customRangeFilter={customRangeFilter}
          setDateFilter={setDateFilter}
          setCustomRangeFilter={setCustomRangeFilter}
          clearLocalData={clearLocalData}
        />
        {marketFilters && (
          <div id="filters_breadcrumbs" className={`mt-[12px]`}>
            <FilterList filters={marketFilters} />
          </div>
        )}
        <div className="flex justify-between mt-1 mb-2.5 py-[21px] px-0 pb-0">
          <div className="flex items-center text-center">
            <div className="flex items-center cursor-pointer mr-3" onClick={() => history.goBack()}>
              <CrumbIcon className="mr-[5px] h-3 fill-current text-navy" />
              <p className="flex items-center font-nunitoBold text-navy m-0 text-sm">{copyData?.overview_title}</p>
            </div>
            {handleCrumbRendering()}
          </div>
        </div>
        <Grid colGap={'4px'} rowGap="4px" className="mb-1" gridCols={calculateSegmentGrid()[0]}>
          {allSegmentsData && allSegmentsData.length > 0 ? (
            <>
              {!displayNoData ? (
                allSegmentsData.map((segment, index) => {
                  if (segment !== undefined) {
                    if (client === 'demo' && segment.title.toUpperCase() === 'SPEND AS % OF GROSS <BR/> REVENUE') {
                      segment.currentValue = 8.25;
                      segment.yoyPercentage = 17.81;
                      segment.ytdPercentage = 24.82;
                      segment.momPercentage = -22.58;
                    }
                    const currency =
                      !segment.currencyCode && !segment.unit ? data.selectedCurrency : segment.currencyCode;
                    // const hasCurrency = segment.currencyCode !== undefined;
                    return (
                      <SegmentContainer
                        key={segment.title}
                        className={index + 1 === calculateSegmentGrid()[1] ? 'mr-0' : ''}
                        variant={chosenSegmentIndex === segment.index ? 'chosen' : ''}
                        onClick={() => handleSegmentClick(segment.index)}
                      >
                        <Segment
                          variant="sectionDetail"
                          title={segment.title.toUpperCase()}
                          totalValue={segment.currentValue}
                          currency={segment.currentValueType === 'currency' ? currency : undefined}
                          unit={segment.currentValueType === 'percentage' ? '%' : ''}
                          YoY={segment.yoyPercentage}
                          YtD={segment.ytdPercentage}
                          last13={segment.l13wPercentage}
                          last52={segment.l52wPercentage}
                          MoM={segment.momPercentage}
                          target={segment.targetPercentage}
                          filters={marketFilters}
                          displayData={
                            !showPlanPercentForNetSales && segment.title === 'Net Sales'
                              ? ['MoM', 'YoY']
                              : segment.displayData
                          }
                          tooltipData={
                            segment.sectionDetailsTooltip
                              ? {
                                  status: 'PARTIAL',
                                  message:
                                    segment.title.toUpperCase() === 'AOV'
                                      ? showWidgets
                                        ? copyData.aov_ams_tooltip
                                        : copyData.aov_non_ams_tooltip
                                      : segment.title.toUpperCase() === 'POS'
                                      ? copyData.pos_tooltip
                                      : copyData.netsales_tooltip
                                }
                              : null
                          }
                        />
                      </SegmentContainer>
                    );
                  }
                })
              ) : (
                <p className="m-0 font-nunitoRegular text-fontBlock text-sm mb-[15px]">{copyData?.no_data}</p>
              )}
            </>
          ) : (
            <p className="m-0 font-nunitoRegular text-fontBlock text-xs mb-[15px]">{copyData?.loading}</p>
          )}
        </Grid>
        <SectionDetailGraph
          marketFilters={marketFilters}
          chosenSegmentIndex={chosenSegmentIndex}
          selectedCurrency={selectedCurrency}
          dateFilter={dateFilter}
          customRangeFilter={customRangeFilter}
          currencyCode={selectedCurrency}
          setStatus={setStatus}
          copyData={copyData}
          showPlanPercentForNetSales={showPlanPercentForNetSales}
          showWidgets={showWidgets}
        />
      </div>
      <div className="mt-2">
        <SectionDetailGrid
          marketFilters={marketFilters}
          chosenSegmentIndex={chosenSegmentIndex}
          selectedCurrency={selectedCurrency}
          dateFilter={dateFilter}
          customRangeFilter={customRangeFilter}
          setStatus={setStatus}
          copyData={copyData}
          showWidgets={showWidgets}
        />
      </div>
    </div>
  );
};

export default SectionDetail;
